export type PermissionId = string | number

export type Permission = {
  id_feature: PermissionId
  title: string
  name: string
  route?: string
  type?: string
}

export type PermissionRisk = 'standard' | 'sensitive' | 'critical'

export const AGENT_STUDIO_ACCESS = 'agent-studio-view'

export const CATEGORY_LABELS: Record<string, string> = {
  'agent-studio': 'Agent Studio',
  unilever: 'Unilever',
  cashback: 'Sistema de Cashback',
  logs: 'Sistema de Logs',
  ai: 'Inteligência Artificial',
  users: 'Controle de Usuários',
  nps: 'Gerenciador de Enquetes',
  campaign: 'Simulador de Campanhas',
  conecta: 'Conecta',
  datatransfer: 'Data Transfer',
  reports: 'Relatórios',
  api: 'API',
  briefing: 'Briefing Promoções',
  'briefing-topicos': 'Briefing Promoções - Tópicos',
  seller: 'Seller Mapping',
  infrapay: 'Infrapay',
  devops: 'DevOps',
  settings: 'Configurações',
}

const AGENT_STUDIO_MODULES = [
  {
    key: 'access',
    label: 'Acesso geral',
    description: 'Libera a entrada no módulo e serve de base para todas as demais permissões.',
  },
  {key: 'dashboard', label: 'Dashboard', description: 'Visão inicial e indicadores operacionais.'},
  {
    key: 'activities',
    label: 'Minhas Atividades',
    description: 'Pendências, aprovações e pedidos de saldo.',
  },
  {
    key: 'agents',
    label: 'Agentes',
    description: 'Consulta, configuração, chat e exclusão de agentes.',
  },
  {key: 'flows', label: 'Fluxos', description: 'Consulta, edição, execução e exclusão de fluxos.'},
  {
    key: 'knowledge-bases',
    label: 'Bases de Conhecimento',
    description: 'Bases, documentos e conteúdo usado pelos agentes.',
  },
  {key: 'runs', label: 'Execuções', description: 'Histórico e detalhes das execuções.'},
  {
    key: 'budget',
    label: 'Áreas & Budget',
    description: 'Áreas, pessoas, limites, modelos e solicitações de saldo.',
  },
  {key: 'costs', label: 'Custos', description: 'Custos consolidados e dados de auditoria.'},
  {
    key: 'users',
    label: 'Usuários',
    description: 'Administração de usuários vinculados ao Agent Studio.',
  },
] as const

export type AgentStudioModule = typeof AGENT_STUDIO_MODULES[number]

const CRITICAL_PERMISSIONS = new Set([
  'agent-studio-activities-decide',
  'agent-studio-agents-delete',
  'agent-studio-flows-delete',
  'agent-studio-knowledge-bases-delete',
  'agent-studio-budget-manage',
  'agent-studio-users-manage',
])

const SENSITIVE_PERMISSIONS = new Set([
  'agent-studio-agents-manage',
  'agent-studio-agents-chat',
  'agent-studio-flows-manage',
  'agent-studio-flows-execute',
  'agent-studio-knowledge-bases-manage',
  'agent-studio-budget-request',
  'agent-studio-costs-view',
])

const DEPENDENCIES: Record<string, string[]> = {
  'agent-studio-activities-decide': ['agent-studio-activities-view'],
  'agent-studio-agents-manage': ['agent-studio-agents-view'],
  'agent-studio-agents-delete': ['agent-studio-agents-view'],
  'agent-studio-agents-chat': ['agent-studio-agents-view'],
  'agent-studio-flows-manage': ['agent-studio-flows-view'],
  'agent-studio-flows-delete': ['agent-studio-flows-view'],
  'agent-studio-flows-execute': ['agent-studio-flows-view', 'agent-studio-flows-manage'],
  'agent-studio-knowledge-bases-manage': ['agent-studio-knowledge-bases-view'],
  'agent-studio-knowledge-bases-delete': ['agent-studio-knowledge-bases-view'],
  'agent-studio-budget-manage': ['agent-studio-budget-view'],
}

export function isAgentStudioPermission(permissionOrName: Permission | string) {
  const name = typeof permissionOrName === 'string' ? permissionOrName : permissionOrName.name
  return name === AGENT_STUDIO_ACCESS || name.startsWith('agent-studio-')
}

export function getPermissionCategory(name: string) {
  if (isAgentStudioPermission(name)) return 'agent-studio'
  if (name.startsWith('briefing-promocoes-topicos-')) return 'briefing-topicos'
  return name.split('-')[0] || 'outros'
}

export function getCategoryLabel(category: string) {
  return CATEGORY_LABELS[category] || category
}

export function getAgentStudioModule(name: string): AgentStudioModule {
  if (name === AGENT_STUDIO_ACCESS) return AGENT_STUDIO_MODULES[0]

  const suffix = name.replace(/^agent-studio-/, '')
  const module = AGENT_STUDIO_MODULES.find(
    (item) => item.key !== 'access' && (suffix === item.key || suffix.startsWith(`${item.key}-`))
  )

  return module || AGENT_STUDIO_MODULES[0]
}

export function getAgentStudioModuleOrder(name: string) {
  const module = getAgentStudioModule(name)
  return AGENT_STUDIO_MODULES.findIndex((item) => item.key === module.key)
}

export function getPermissionRisk(name: string): PermissionRisk {
  if (CRITICAL_PERMISSIONS.has(name)) return 'critical'
  if (SENSITIVE_PERMISSIONS.has(name)) return 'sensitive'
  return 'standard'
}

export function getPermissionDependencies(name: string) {
  if (!isAgentStudioPermission(name) || name === AGENT_STUDIO_ACCESS) return []
  return [AGENT_STUDIO_ACCESS, ...(DEPENDENCIES[name] || [])]
}

export function isAgentStudioScreenPermission(name: string) {
  return name === AGENT_STUDIO_ACCESS || name.endsWith('-view')
}
